import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import Logo from '../Logo';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/contact', label: 'Contact' },
];

const LIGHT_HERO_PATHS = [
  '/services', '/pricing', '/about', '/contact', '/faqs', '/resources',
  '/privacy', '/cookies', '/terms', '/service-terms', '/refunds', '/accessibility',
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onDark = !scrolled && !open && !LIGHT_HERO_PATHS.includes(location.pathname);

  const linkClass = ({ isActive }) =>
    `rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
      isActive
        ? onDark
          ? 'bg-offwhite text-charcoal'
          : 'bg-charcoal text-offwhite'
        : onDark
          ? 'text-offwhite/75 hover:bg-offwhite/10 hover:text-offwhite'
          : 'text-charcoal/75 hover:bg-charcoal/5 hover:text-charcoal'
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled || open
          ? 'border-hairline bg-offwhite/90 shadow-[0_8px_30px_-18px_rgba(36,39,33,0.3)] backdrop-blur-xl'
          : 'border-transparent bg-transparent'
      }`}
      data-testid="site-header"
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-lime focus:px-5 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-charcoal"
        data-testid="skip-to-content"
      >
        Skip to content
      </a>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-[72px] sm:px-6 lg:px-8">
        <Link to="/" aria-label="Build My Trade — home" data-testid="header-logo-link">
          <Logo dark={onDark} />
        </Link>
        <nav aria-label="Main navigation" className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} className={linkClass} data-testid={`nav-${item.label.toLowerCase()}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-quote-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 font-heading text-sm font-bold text-offwhite transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-charcoal-surface"
          >
            Get a Free Quote
            <ArrowRight className="h-4 w-4 text-lime transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          data-testid="mobile-menu-toggle"
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
            onDark ? 'border-offwhite/30 text-offwhite' : 'border-charcoal/15 text-charcoal'
          }`}
        >
          {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div id="mobile-menu" className="border-t border-hairline bg-offwhite lg:hidden" data-testid="mobile-menu">
          <nav aria-label="Mobile navigation" className="mx-auto max-w-7xl space-y-1 px-4 py-4 sm:px-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 font-heading text-lg font-bold tracking-tight transition-colors ${
                    isActive ? 'bg-charcoal text-offwhite' : 'text-charcoal hover:bg-charcoal/5'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3">
              <Link
                to="/contact"
                data-testid="mobile-quote-cta"
                className="flex items-center justify-center gap-2 rounded-full bg-lime px-5 py-3.5 font-heading text-base font-bold text-charcoal"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
